import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  MyTrainingsSessionFragment,
  MyTrainingsTrainingFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Sidepanel from 'modules/core/components/Sidepanel'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { useSessionsForm } from 'modules/trainings/hooks/Sessions/useSessionsForm'
import { useTrainingsDatatable } from 'modules/trainings/hooks/useTrainingsDatatable'

interface Props {
  title: React.ReactNode
  to: string
  session: MyTrainingsSessionFragment
  training: MyTrainingsTrainingFragment
  certificatesModalPath: string
}

export const SessionDetail = ({ to, title, training, session, certificatesModalPath }: Props) => {
  const navigate = useNavigate()
  const { getModalityTranslation } = useTrainingsDatatable()
  const { setHourFromBackend } = useSessionsForm()

  return (
    <Sidepanel open title={title} onClose={() => navigate(to)} closeLabel="X">
      <Box gap="s16">
        <TextField
          readonly
          label={i18n.t('trainings.my_training.all_courses_table.training')}
          value={training.name}
        />
        <TextField
          readonly
          label={i18n.t('trainings.my_training.next_session.session')}
          value={session.name}
        />
        <TextField
          readonly
          label={i18n.t('trainings.sessions_table.modality')}
          value={getModalityTranslation(session.modality)}
        />
        {session.modality && (session.modality === 'online' || session.modality === 'mixed') && (
          <Box
            cursor="pointer"
            onClick={
              session.link
                ? () => window.open(`https://${session.link?.replace('https://', '')}`, '_blank')
                : () => null
            }
            flexDirection="column"
            gap="s4"
          >
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('trainings.create_sessions_modal.link')}
            </Text.Normal>
            <Box
              border={{ all: { color: 'grey400', width: 's2', style: 'solid' } }}
              borderRadius={{ all: 'abs006' }}
              padding="s8"
              background="grey200"
              overflowX="auto"
            >
              <Text.Normal underlined size="300" weight="normal" color="viridian1200">
                {session.link}
              </Text.Normal>
            </Box>
          </Box>
        )}
        {session.modality && (session.modality === 'inperson' || session.modality === 'mixed') && (
          <TextField
            readonly
            label={i18n.t('trainings.sessions_table.location')}
            value={session.location ?? ''}
          />
        )}
        {session.startsAt && (
          <Box flexDirection="row" gap="s16">
            <DayPicker
              value={moment(session?.startsAt).format('L')}
              label={i18n.t('trainings.sessions_modal.start_day')}
              onChange={() => null}
              readonly
            />
            <TextField
              value={setHourFromBackend(session?.startsAt)}
              label={i18n.t('trainings.sessions_modal.start_hour')}
              type="time"
              readonly
            />
          </Box>
        )}
        {session.endsAt && (
          <Box flexDirection="row" gap="s16">
            <DayPicker
              value={moment(session.endsAt).format('L')}
              label={i18n.t('trainings.sessions_modal.end_day')}
              onChange={() => null}
              readonly
            />
            <TextField
              value={setHourFromBackend(session.endsAt)}
              label={i18n.t('trainings.sessions_modal.end_hour')}
              type="time"
              readonly
            />
          </Box>
        )}
        {session.dueDate && (
          <DayPicker
            value={moment(session.dueDate).format('L')}
            label={i18n.t('trainings.sessions_modal.due_date')}
            onChange={() => null}
            readonly
          />
        )}
        <Box gap="s6">
          <Box paddingTop="s24">
            <Button.Router to={certificatesModalPath} iconName="upload">
              {i18n.t('trainings.my_training.upload_certificate.upload')}
            </Button.Router>
          </Box>
        </Box>
      </Box>
    </Sidepanel>
  )
}
